import axiosLib, { AxiosError, AxiosRequestConfig } from "axios";
import { MaxRetriesError, SessionExpiredError } from "~/helpers/errors";
import {
  getJWT,
  getRefreshToken,
  setJWT,
  setRefreshToken,
  isTokenExpired,
} from "~/helpers/auth";
import { paths } from "~/router/paths";
import { excludePaths } from "~/constants/paths";
import { endpointsToExcludeTheJWT } from "~/api/constants";
import { getNow, trackApiRequest } from "~/helpers/datadog";
import { noteFailure, noteSuccess } from "~/helpers/maintenance";
const apiURL: string = import.meta.env.VITE_API_URL;
const s3Hostname = "s3.amazonaws.com";

export const axios = axiosLib.create({
  timeout: 60000,
});

const getURL = (path: string) =>
  `${apiURL}/${path.endsWith("/") ? path : path + "/"}`;

type DatadogAxiosConfig = AxiosRequestConfig & {
  metadata?: {
    requestStartTime: number;
  };
};

const getRequestDuration = (config?: AxiosRequestConfig) => {
  const requestStartTime = (config as DatadogAxiosConfig | undefined)?.metadata
    ?.requestStartTime;

  if (!requestStartTime) return 0;
  return getNow() - requestStartTime;
};

const isAllowedS3Endpoint = (url?: string): boolean => {
  if (!url) return false;

  try {
    const { hostname } = new URL(url, apiURL);
    return hostname === s3Hostname || hostname.endsWith(`.${s3Hostname}`);
  } catch {
    return false;
  }
};

const isJWTExcludedEndpoint = (url?: string): boolean =>
  !!url && endpointsToExcludeTheJWT.some((endpoint) => url.includes(endpoint));

// The one in-flight refresh. Every caller that needs a fresh access token
// awaits this same promise, so a page-load batch of N requests produces one
// refresh call rather than N (Monday ticket 12857922247).
let inFlightRefresh: Promise<string | null> | null = null;

const requestNewAccessToken = async (): Promise<string | null> => {
  const refresh = getRefreshToken();
  if (!refresh) return null;

  try {
    // A bare axios call on purpose. Going through sendPost would re-enter
    // handleErrorWrapper, which retries and signs out on its own — making the
    // outcome of a refresh impossible to reason about from here, and nesting
    // one retry budget inside another.
    const res = await axios.post(getURL("auth/token/refresh/"), { refresh });
    const access = res?.data?.access;
    if (!access) return null;

    setJWT(access);
    return access;
  } catch {
    // Includes the 401 that means the refresh token is itself dead. Callers
    // treat null as "stop, this cannot be retried".
    return null;
  }
};

/**
 * Fresh access token, refreshing at most once across concurrent callers.
 *
 * Resolves to null when the refresh was refused, which is not retryable: the
 * caller's job is then to sign the user out, not to try again.
 */
export const ensureFreshToken = (): Promise<string | null> => {
  if (!inFlightRefresh) {
    inFlightRefresh = requestNewAccessToken().finally(() => {
      inFlightRefresh = null;
    });
  }

  return inFlightRefresh;
};

const handleRequest = async (config: AxiosRequestConfig): Promise<any> => {
  (config as DatadogAxiosConfig).metadata = { requestStartTime: getNow() };

  const isAuthEndpoint = isJWTExcludedEndpoint(config.url);
  const isS3Endpoint = isAllowedS3Endpoint(config.url);

  if (isS3Endpoint || isAuthEndpoint) {
    return config;
  }

  let jwt = getJWT();

  // Refresh ahead of the request rather than after a 401 comes back. On a page
  // load every request in the batch reaches here holding the same expired
  // token; ensureFreshToken collapses them onto one refresh, so the batch
  // produces no 401s at all instead of one per request.
  if (isTokenExpired(jwt) && getRefreshToken()) {
    jwt = await ensureFreshToken();

    if (!jwt) {
      // The refresh token was refused. No retry can help, so stop here and
      // send the user to login rather than letting the batch spin on 401s.
      forceSignOut();
      throw new SessionExpiredError();
    }
  }

  if (jwt && config.headers) config.headers.Authorization = `Bearer ${jwt}`;
  return config;
};

axios.interceptors.request.use(handleRequest);
axios.interceptors.response.use(
  (response) => {
    trackApiRequest({
      method: response.config.method,
      url: response.config.url,
      status: response.status,
      durationMs: getRequestDuration(response.config),
    });

    noteSuccess();

    return response;
  },
  (error: AxiosError) => {
    trackApiRequest({
      method: error.config?.method,
      url: error.config?.url,
      status: error.response?.status,
      durationMs: getRequestDuration(error.config),
      errorMessage: error.message,
    });

    // Fire and forget — detection must never delay or replace the rejection.
    void noteFailure(error.response?.status, error.config?.url);

    return Promise.reject(error);
  }
);

/**
 * Raw-`fetch` probe used by the maintenance page's recovery loop
 * (`startRecovery`, wired in main.tsx). Deliberately NOT `sendGet`/`axios`:
 * this app's own request interceptor (`handleRequest` above) can call
 * `forceSignOut()` from local token state before any network request is even
 * made, which would log the user out purely from polling during an outage.
 * A bare `fetch` never enters that interceptor and carries no Authorization
 * header, so it is unaffected by `core/health_check.py` on stylesi-api
 * currently missing the `AllowAny`/no-auth decorators its siblings have
 * (that gap only bites a caller that sends a stale token; this probe never
 * sends one). Any response at all — even a 401 — proves Django and its
 * middleware stack are up, which is all readiness means here.
 */
export const confirmReadiness = async (): Promise<boolean> => {
  try {
    const res = await fetch(`${apiURL}/health_check/`, {
      cache: "no-store",
      credentials: "omit",
      signal: AbortSignal.timeout(5000),
    });
    return res.status < 500;
  } catch {
    return false;
  }
};

export const forceSignOut = () => {
  if (
    excludePaths.find((path) => new RegExp(path).test(window.location.pathname))
  ) {
    return; // Prevent sign out on pages where the user is not logged in.
  }

  setJWT("");
  setRefreshToken("");
  window.location.href = paths.auth.login;
};

const handleErrorWrapper = async <T = any>(
  next: Function,
  retries: number = 5
): Promise<T | void> => {
  if (retries <= 0) {
    throw new MaxRetriesError("Max retries reached");
  }

  try {
    return await next();
  } catch (err: unknown) {
    if (err instanceof AxiosError) {
      if (err.response) {
        const { status } = err.response;

        if (status === 402) {
          localStorage.setItem("plansModal", "true");
        }

        if (status === 401 && !isJWTExcludedEndpoint(err?.config?.url)) {
          const refresh = getRefreshToken();

          // A 401 from the refresh endpoint itself is terminal — the refresh
          // token is dead, so retrying only produces more 401s.
          if (err?.config?.url?.includes("/token/refresh/")) {
            return forceSignOut();
          }

          if (!refresh) {
            return forceSignOut();
          }

          // Shared with every other request that raced into this branch, so
          // one expired token still costs exactly one refresh call.
          const access = await ensureFreshToken();
          if (!access) return forceSignOut();

          return handleErrorWrapper(next, retries - 1);
        }
      }
    }
    return Promise.reject(err);
  }
};

export const sendGet = async (path: string, params?: Object): Promise<any> => {
  return handleErrorWrapper(async () => {
    return axios.get(getURL(path), { params });
  });
};

export const sendPost = async (path: string, params?: Object) => {
  return handleErrorWrapper(async () => {
    return axios.post(getURL(path), params);
  });
};

export const sendPut = async (path: string, params?: Object) => {
  return handleErrorWrapper(async () => {
    return axios.put(getURL(path), params);
  });
};

export const sendDelete = async (path: string, params: Object) => {
  return handleErrorWrapper(async () => {
    return axios.delete(getURL(path), params);
  });
};
